import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { ArrowBackIos, ArrowForwardIos } from '@material-ui/icons';
import NotificationIcon from 'assets/icons/NotificationIcon';
import NotificationItem from 'components/NotificationItem';
import {
  CONTRATO_ETAPAS_PARAMS_URL,
  CONTRATO_PASSOS,
  CONTRATO_FLUXO_DESCENTRALIZADO,
  ContratoStatus,
  TipoContrato,
  TipoFluxo,
} from 'models/contratos';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteNotifications,
  fetchNotifications,
  setCurrentPage,
} from 'store/modules/NotificationsActions';
import SnackbarUtils from 'utils/SnackbarUtils';
import {
  mapStatusToText,
  mapTipoContratoText,
  mapTipoText,
} from 'utils/notificationUtils';
import './styles.scss';
import { Notification, NotificationsState } from './__types';
import { newContratoDescentralizadoEditRoute } from 'library/Routes';
import { RootState } from 'store';
import { useCallback, useMemo } from 'react';

interface NotificationItemProps {
  type: string;
  description: React.ReactNode;
  toCloseNotificationsMenu: () => void;
  anchorNotificationEl: HTMLElement;
}

const Notifications = ({
  toCloseNotificationsMenu,
  anchorNotificationEl,
}: NotificationItemProps) => {
  const {
    notifications,
    currentPage,
    totalPages,
    itemPerPage,
    totalNotifications,
    loading,
  } = useSelector(
    (state: { notifications: NotificationsState }) => state.notifications
  );
  const languageValues = useSelector((state: RootState) => state.languages.languageValues);
  const dispatch = useDispatch();

  const menuNameStyle = useMemo(() => (
    notifications.length === 0 ? { borderBottom: 'none' } : {}
  ), [notifications.length]);

  const showPaginationButtons = useMemo(() => (
    notifications.length > 0 && totalPages > 1 && !loading
  ), [notifications.length, totalPages, loading]);

  const limpaNotificacoes = useCallback(() => {
    const notificationIds = notifications.map(
      (notification: { id: string }) => notification.id
    );
    toCloseNotificationsMenu();
    dispatch(deleteNotifications(notificationIds));
    localStorage.setItem('notificationCount', '0');
  }, [notifications, toCloseNotificationsMenu, dispatch]);

  function URL_redirecionamento_assessor(item: Notification) {
    const basePathAssessor = '/programaseducacionais/admin/contrato/assessor';
    let url;

    const isCentralizado = item.tipoFluxo === TipoFluxo.CENTRALIZADO;
    const hasAditivo = typeof item.idAditivo === 'number';
    const idContratoPath = `${basePathAssessor}/${item.idContrato}`;

    if (isCentralizado) {
      if (hasAditivo) {
        switch (item.flgStatus) {
          case ContratoStatus.AGUARDANDO_ASSINATURA:
            url = `${idContratoPath}/aditivo/${item.idAditivo}/validacao`;
            break;
          case ContratoStatus.ENCERRADO:
          case ContratoStatus.EM_EDICAO:
            url = `${idContratoPath}/aditivo/${item.idAditivo}/dados`;
            break;
          case ContratoStatus.EM_ANALISE:
            url = `${idContratoPath}/aditivo/${item.idAditivo}/validacao`;
            break;
          default:
            break;
        }
      } else {
        switch (item.flgStatus) {
          case ContratoStatus.EM_EDICAO:
            url = `${idContratoPath}/dados`;
            break;
          case ContratoStatus.ENCERRADO:
          case ContratoStatus.EM_ANALISE:
            url = `${idContratoPath}/validacao`;
            break;
          default:
            break;
        }
      }
    } else if (item.tipoFluxo === TipoFluxo.DESCENTRALIZADO) {
      const notificacaoParam = `${item.idAditivo}`;
      if (hasAditivo && item.flgStatus === ContratoStatus.ENCERRADO) {
        url = newContratoDescentralizadoEditRoute.getURLWithParams(
          CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR,
          CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
          item.idContrato,
          CONTRATO_PASSOS.DOCUMENTOS,
          notificacaoParam
        );
      } else if (item.flgStatus === ContratoStatus.ENCERRADO) {
        url = newContratoDescentralizadoEditRoute.getURLWithParams(
          CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR,
          CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
          item.idContrato,
          CONTRATO_PASSOS.DOCUMENTOS
        );
      }
    }
    return url;
  }

  function URL_redirecionamento_entePublico(item: Notification) {
    const basePath = '/programaseducacionais/admin/contrato';
    const tipoContratoTexto = mapTipoContratoText(TipoContrato.ENTE_PUBLICO);
    let url = `${basePath}/${tipoContratoTexto}`;

    switch (item.tipoFluxo) {
      case TipoFluxo.DESCENTRALIZADO:
        url = entePublicoDescentralizado(item) || url;
        break;
      case TipoFluxo.CENTRALIZADO:
        url += entePublicoCentralizado(item);
        break;
      default:
        url += `/${item.idContrato}`;
    }
    return url;
  }

  function entePublicoCentralizado(item: Notification): string {
    const idContratoPath = `/${item.idContrato}`;
    const hasAditivo = typeof item.idAditivo === 'number';

    if (hasAditivo) {
      switch (item.flgStatus) {
        case ContratoStatus.AGUARDANDO_ASSINATURA:
          return `${idContratoPath}/aditivo/${item.idAditivo}/validacao`;
        case ContratoStatus.ENCERRADO:
        case ContratoStatus.EM_EDICAO:
          return `${idContratoPath}/aditivo/${item.idAditivo}/dados`;
        case ContratoStatus.EM_ANALISE:
          return `${idContratoPath}/aditivo/${item.idAditivo}/validacao`;
        default:
          return idContratoPath;
      }
    } else {
      switch (item.flgStatus) {
        case ContratoStatus.EM_EDICAO:
          return `${idContratoPath}/dados`;
        case ContratoStatus.ENCERRADO:
        case ContratoStatus.EM_ANALISE:
          return `${idContratoPath}/validacao`;
        default:
          return idContratoPath;
      }
    }
  }

  function entePublicoDescentralizado(item: Notification): string {
    switch (item.flgStatus) {
      case ContratoStatus.ENCERRADO:
      case ContratoStatus.AGUARDANDO_EXTRATO:
        return newContratoDescentralizadoEditRoute.getURLWithParams(
          CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PUBLICO,
          CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
          item.idContrato,
          CONTRATO_PASSOS.DOCUMENTOS
        );
      default:
        SnackbarUtils.error(languageValues.erroRotaEntePublicoDescentralizado);
        return '';
    }
  }

  function URL_redirecionamento_entePrivado(item: Notification) {
    let url;
    if (item.tipoFluxo === TipoFluxo.DESCENTRALIZADO) {
      return newContratoDescentralizadoEditRoute.getURLWithParams(
        CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO,
        CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
        item.idContrato,
        CONTRATO_PASSOS.DOCUMENTOS
      );
    }
    return url;
  }

  function final_URLs(tipoContrato: TipoContrato) {
    const urlBuilders: {
      [key: string]: (item: Notification) => string | undefined;
    } = {
      [TipoContrato.ASSESSOR.toString()]: URL_redirecionamento_assessor,
      [TipoContrato.ENTE_PUBLICO.toString()]: URL_redirecionamento_entePublico,
      [TipoContrato.ENTE_PRIVADO.toString()]: URL_redirecionamento_entePrivado,
    };
    return urlBuilders[tipoContrato.toString()];
  }

  const notificacaoClicada = (item: Notification) => {
    toCloseNotificationsMenu();
    if (item.tipoContrato === undefined) {
      SnackbarUtils.error(languageValues.tipoDeContratoNaoDefinido);
      return;
    }
    const buildUrl = final_URLs(item.tipoContrato);
    const url = buildUrl(item);
    if (url) {
      window.open(url, '_blank');
    } else {
      SnackbarUtils.error(languageValues.NenhumaRotaEncontradaParaEstaNotificacao);
    }
    setTimeout(() => {
      toCloseNotificationsMenu();
    }, 100);
  };

  const goToNextPage = useCallback(() => {
    const newPage = currentPage + 1;
    if (newPage < totalPages) {
      dispatch(setCurrentPage(newPage));
      dispatch(fetchNotifications(newPage, itemPerPage));
    }
  }, [currentPage, dispatch, itemPerPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    const newPage = Math.max(currentPage - 1, 0);
    dispatch(setCurrentPage(newPage));
    dispatch(fetchNotifications(newPage, itemPerPage));
  }, [currentPage, dispatch, itemPerPage]);

  const getNotificationCounterText = () => {
    const start = currentPage * itemPerPage + 1;
    const end = Math.min(start + itemPerPage - 1, totalNotifications);
    return `${start} -${end} de ${totalNotifications} itens`;
  };

  const renderNotificationsMenuItems = () => {
    if (loading && notifications.length > 0) {
      return (
        <MenuItem style={{ minWidth: '400px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '20px 0 20px 0',
            }}
          >
            <CircularProgress
              style={{ width: '20px', height: '20px' }}
              className='margin-right-4'
            />
            <Typography style={{ marginLeft: '15px' }}>
              {languageValues.buscandoNotificacoes}
            </Typography>
          </div>
        </MenuItem>
      );
    }

    if (notifications.length === 0) return loading;
    return notifications.map((item: Notification) => (
      <MenuItem
        key={item.id}
        style={{ borderBottom: '1px solid #D8D8D8', whiteSpace: 'normal' }}
      >
        <NotificationItem
          type={mapTipoText(item.tipo)}
          description={
            <span>
              {`O contrato Nº: ${item.descricao}, foi alterado para: `}
              <span className='status'>{mapStatusToText(item.flgStatus)}</span>
            </span>
          }
          onClick={() => notificacaoClicada(item)}
        />
      </MenuItem>
    ));
  };

  return (
    <Menu
      id='notificationsMenu'
      anchorEl={anchorNotificationEl}
      open={Boolean(anchorNotificationEl)}
      keepMounted
      onClose={toCloseNotificationsMenu}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      className='CustomMenuNotifications'
    >
      <div className='container-menuName' style={menuNameStyle}>
        <div className='container-menuTitle'>
          <div className='container-menuIcon'>
            <NotificationIcon />
          </div>
          {notifications.length > 0
            ? languageValues.notificacoes
            : languageValues.naoHaNotificacoesNovas
          }
        </div>
        {notifications.length > 0 && (
          <Button
            className={'container-clear'}
            disabled={loading}
            onClick={limpaNotificacoes}
          >
            {languageValues.limparNotificacoes}
          </Button>
        )}
      </div>
      <div className='modal-notifications'>
        {renderNotificationsMenuItems()}

        {showPaginationButtons ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-evenly',
              padding: '8px 0 0 0',
            }}
          >
            <MenuItem>
              <Typography style={{ fontSize: '13px' }}>
                {getNotificationCounterText()}
              </Typography>
            </MenuItem>
            <MenuItem onClick={goToPreviousPage} disabled={currentPage <= 0}>
              <Typography className='container-typography'>
                <ArrowBackIos style={{ height: '14px', width: '15px' }} />
                {languageValues.anterior}
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={goToNextPage}
              disabled={currentPage >= totalPages - 1}
            >
              <Typography className='container-typography'>
                {languageValues.proxima}
                <ArrowForwardIos style={{ height: '14px', width: '16px' }} />
              </Typography>
            </MenuItem>
          </div>
        ) : null}
      </div>
    </Menu>
  );
};

export default Notifications;
